// Pure helpers behind the Map Builder's blueprint canvas: mapping between
// canvas pixels and world metres, snapping drags into wall segments and floor
// panels, hit-testing structure for the eraser, and floor creation. All pure
// so the drawing tools are unit-testable without a DOM.
import type {
  MapFloorDocument,
  MapFloorLevel,
  MapFloorPanel,
  MapWallSegment,
} from '../content/schema/map';

/** How the blueprint canvas frames the world: a centred square viewport
 * showing `extent` metres from centre to the nearest edge. */
export interface BlueprintViewport {
  width: number;
  height: number;
  /** World metres from canvas centre to the nearest canvas edge. */
  extent: number;
}

/** Pixels per world metre for a viewport. */
export function blueprintScale(viewport: BlueprintViewport): number {
  return Math.min(viewport.width, viewport.height) / 2 / viewport.extent;
}

/** Canvas pixel -> world ground-plane point (x, z). */
export function worldFromCanvas(
  px: number,
  py: number,
  viewport: BlueprintViewport,
): [number, number] {
  const scale = blueprintScale(viewport);
  return [(px - viewport.width / 2) / scale, (py - viewport.height / 2) / scale];
}

/** World ground-plane point -> canvas pixel. */
export function canvasFromWorld(
  x: number,
  z: number,
  viewport: BlueprintViewport,
): [number, number] {
  const scale = blueprintScale(viewport);
  return [viewport.width / 2 + x * scale, viewport.height / 2 + z * scale];
}

/** Snap a world coordinate to the grid step (no-op for step <= 0). */
export function snapToGrid(value: number, step: number): number {
  if (!(step > 0)) return value;
  return Math.round(value / step) * step;
}

export interface WallDragOptions {
  id: string;
  snap: number;
  height: number;
  thickness: number;
  color?: string;
  /**
   * Draw at the drag's own angle instead of locking to the dominant axis.
   * Endpoints still snap to the grid; the wall's `rotationY` carries the
   * angle. The renderer and the 3D collision footprint (an axis-aligned
   * bounding box for a rotated wall - see `makeWalkable`'s doc comment)
   * already support this; only the drawing tool defaulted to axis-locked.
   */
  freeAngle?: boolean;
}

/**
 * A wall segment from a click-drag on the ground plane: endpoints snap to the
 * grid. By default the wall axis-locks to the dominant drag direction (the
 * blockout convention); pass `freeAngle` to draw at the exact angle between
 * the snapped endpoints instead. Drags shorter than one grid step (or one
 * metre on a snapless floor) produce no wall.
 */
export function wallFromDrag(
  from: [number, number],
  to: [number, number],
  options: WallDragOptions,
): MapWallSegment | null {
  const step = options.snap > 0 ? options.snap : 0.5;
  const ax = snapToGrid(from[0], step);
  const az = snapToGrid(from[1], step);
  const bx = snapToGrid(to[0], step);
  const bz = snapToGrid(to[1], step);

  if (options.freeAngle) {
    const dx = bx - ax;
    const dz = bz - az;
    const length = Math.hypot(dx, dz);
    if (length < step) return null;
    // Local +X (the wall's unrotated long axis) maps to world (cos, -sin)
    // under a Three.js rotation.y of `angle` - solve for the angle that
    // points it along the drag vector.
    const angle = Math.atan2(-dz, dx);
    const wall: MapWallSegment = {
      id: options.id,
      position: [(ax + bx) / 2, options.height / 2, (az + bz) / 2],
      size: [length, options.height, options.thickness],
      rotationY: angle,
    };
    if (options.color) wall.color = options.color;
    return wall;
  }

  const alongX = Math.abs(bx - ax) >= Math.abs(bz - az);
  const length = alongX ? Math.abs(bx - ax) : Math.abs(bz - az);
  if (length < step) return null;

  const wall: MapWallSegment = alongX
    ? {
        id: options.id,
        position: [(ax + bx) / 2, options.height / 2, az],
        size: [length, options.height, options.thickness],
      }
    : {
        id: options.id,
        position: [ax, options.height / 2, (az + bz) / 2],
        size: [options.thickness, options.height, length],
      };
  if (options.color) wall.color = options.color;
  return wall;
}

export interface PanelDragOptions {
  id: string;
  snap: number;
  ceilingHeight: number;
  color: string;
}

/**
 * A floor panel from a drag rectangle: corners snap to the grid; rectangles
 * thinner than one grid step in either direction produce no panel.
 */
export function panelFromDrag(
  from: [number, number],
  to: [number, number],
  options: PanelDragOptions,
): MapFloorPanel | null {
  const step = options.snap > 0 ? options.snap : 0.5;
  const ax = snapToGrid(from[0], step);
  const az = snapToGrid(from[1], step);
  const bx = snapToGrid(to[0], step);
  const bz = snapToGrid(to[1], step);
  const width = Math.abs(bx - ax);
  const depth = Math.abs(bz - az);
  if (width < step || depth < step) return null;
  return {
    id: options.id,
    center: [(ax + bx) / 2, (az + bz) / 2],
    size: [width, depth],
    color: options.color,
    ceilingHeight: options.ceilingHeight,
  };
}

export interface StairFootprintOptions {
  snap: number;
}

/** A stair footprint drawn from a click-drag: the rectangle itself, plus the
 * axis/foot derived from the drag direction - mirrors `MapStairRun`'s own
 * `axis`/`footAt`, so a run built directly from this needs no further work. */
export interface StairFootprint {
  center: [number, number];
  size: [number, number];
  /** The drag's dominant direction - the axis a run climbs along. */
  axis: 'x' | 'z';
  /** Which end of `axis` the drag started at: -1 the low coordinate, +1 the high. */
  footAt: -1 | 1;
}

/**
 * A stair footprint from a click-drag on the ground plane, snapped to the
 * grid like `panelFromDrag`. The drag's dominant axis (whichever of width/
 * depth is larger) becomes the footprint's climbing axis, and the drag's own
 * start-to-end direction along that axis becomes `footAt` - drawing toward
 * the low coordinate gives `-1`, toward the high coordinate `+1`, matching
 * `MapStairRun.footAt`'s own convention. Drags thinner than one grid step in
 * either direction produce no footprint.
 */
export function stairFootprintFromDrag(
  from: [number, number],
  to: [number, number],
  options: StairFootprintOptions,
): StairFootprint | null {
  const step = options.snap > 0 ? options.snap : 0.5;
  const ax = snapToGrid(from[0], step);
  const az = snapToGrid(from[1], step);
  const bx = snapToGrid(to[0], step);
  const bz = snapToGrid(to[1], step);
  const width = Math.abs(bx - ax);
  const depth = Math.abs(bz - az);
  if (width < step || depth < step) return null;
  const axis: 'x' | 'z' = width >= depth ? 'x' : 'z';
  const axisIndex = axis === 'x' ? 0 : 1;
  const footAt: -1 | 1 = from[axisIndex] <= to[axisIndex] ? -1 : 1;
  return {
    center: [(ax + bx) / 2, (az + bz) / 2],
    size: [width, depth],
    axis,
    footAt,
  };
}

export type StructureHit = { kind: 'wall'; id: string } | { kind: 'panel'; id: string };

/**
 * The structure under a ground-plane point, walls before panels (walls are
 * thinner, so they would otherwise be unreachable over a panel). `tolerance`
 * widens walls for clickability.
 */
export function hitTestStructure(
  point: [number, number],
  floor: Pick<MapFloorDocument, 'walls' | 'panels'>,
  tolerance = 0.3,
): StructureHit | null {
  const [x, z] = point;
  for (const wall of floor.walls) {
    const halfX = wall.size[0] / 2 + tolerance;
    const halfZ = wall.size[2] / 2 + tolerance;
    if (Math.abs(x - wall.position[0]) <= halfX && Math.abs(z - wall.position[2]) <= halfZ) {
      return { kind: 'wall', id: wall.id };
    }
  }
  for (const panel of floor.panels) {
    if (
      Math.abs(x - panel.center[0]) <= panel.size[0] / 2 &&
      Math.abs(z - panel.center[1]) <= panel.size[1] / 2
    ) {
      return { kind: 'panel', id: panel.id };
    }
  }
  return null;
}

/** The next floor above the highest existing one, with a sensible label. */
export function createNextFloor(floors: readonly MapFloorDocument[]): MapFloorDocument {
  const level = floors.length === 0 ? 0 : Math.max(...floors.map((f) => f.level)) + 1;
  return {
    id: `floor-${level}`,
    level,
    label: level === 0 ? 'Ground Floor' : `Floor ${level}`,
    panels: [],
    walls: [],
    blockers: [],
    areas: [],
  };
}

/** Every floor except `level`. Does not touch objects/lights/etc placed on
 * that floor elsewhere in the document - the caller removes those too. */
export function removeFloor(floors: readonly MapFloorDocument[], level: MapFloorLevel): MapFloorDocument[] {
  return floors.filter((f) => f.level !== level);
}

/** The world half-extent that frames every panel and wall of a floor, with
 * headroom to draw beyond the existing structure. */
export function floorExtent(floor: Pick<MapFloorDocument, 'walls' | 'panels'> | null, minimum = 12): number {
  let extent = 0;
  if (floor) {
    for (const panel of floor.panels) {
      extent = Math.max(
        extent,
        Math.abs(panel.center[0]) + panel.size[0] / 2,
        Math.abs(panel.center[1]) + panel.size[1] / 2,
      );
    }
    for (const wall of floor.walls) {
      extent = Math.max(
        extent,
        Math.abs(wall.position[0]) + wall.size[0] / 2,
        Math.abs(wall.position[2]) + wall.size[2] / 2,
      );
    }
  }
  return Math.max(minimum, Math.ceil(extent * 1.25));
}
